import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { notificationsAPI, authAPI, coreAPI, NotificationPreferences } from '../../lib/api';
import { toast } from 'sonner';
import { ProfileFormData } from './types';

export function useSettings() {
  const { user, startImpersonation } = useAuth();

  // Profile form state
  const [profileForm, setProfileForm] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
  });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(null);
  const [loadingNotifPrefs, setLoadingNotifPrefs] = useState(false);
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);

  // Check if user is Helix admin (for Login As feature)
  const isHelixAdmin = user?.platform === 'HELIX' || false;

  const loadProfileData = useCallback(async () => {
    if (!user) return;

    // Pre-populate from user context immediately
    setProfileForm(prev => ({
      ...prev,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      email: user.email || '',
    }));

    // Fetch additional profile data from API
    try {
      setLoadingProfile(true);
      const response = await authAPI.profile();
      const profileData = response.data.data || response.data;

      let phone = '';
      let jobTitle = '';

      // Fetch full UserProfile for phone/job_title
      try {
        const profileRes = await coreAPI.getMyProfile();
        const fullProfile = profileRes.data;
        phone = fullProfile.phone || '';
        jobTitle = fullProfile.job_title || '';
      } catch {
        // Fall back to auth profile data
      }

      setProfileForm({
        firstName: profileData.first_name || user.first_name || '',
        lastName: profileData.last_name || user.last_name || '',
        email: profileData.email || user.email || '',
        phone: phone || profileData.phone || '',
        jobTitle: jobTitle || profileData.job_title || '',
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      // Keep the values from user context
    } finally {
      setLoadingProfile(false);
    }
  }, [user]);

  const loadNotificationPreferences = useCallback(async () => {
    try {
      setLoadingNotifPrefs(true);
      const response = await notificationsAPI.getPreferences();
      setNotifPrefs(response.data);
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    } finally {
      setLoadingNotifPrefs(false);
    }
  }, []);

  useEffect(() => {
    loadProfileData();
  }, [loadProfileData]);

  useEffect(() => {
    loadNotificationPreferences();
  }, [loadNotificationPreferences]);

  const handleProfileChange = (field: keyof ProfileFormData, value: string) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      await authAPI.updateProfile({
        first_name: profileForm.firstName,
        last_name: profileForm.lastName,
        phone: profileForm.phone,
        job_title: profileForm.jobTitle,
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleNotifPrefChange = async (key: keyof NotificationPreferences, value: boolean) => {
    if (!notifPrefs) return;

    const updatedPrefs = { ...notifPrefs, [key]: value };
    setNotifPrefs(updatedPrefs);

    try {
      setSavingNotifPrefs(true);
      await notificationsAPI.updatePreferences({ [key]: value });
    } catch (error) {
      console.error('Failed to update notification preference:', error);
      // Revert on error
      setNotifPrefs(notifPrefs);
      toast.error('Failed to update notification preference');
    } finally {
      setSavingNotifPrefs(false);
    }
  };

  return {
    // User info
    user,
    isHelixAdmin,
    startImpersonation,

    // Profile
    profileForm,
    loadingProfile,
    savingProfile,
    handleProfileChange,
    handleSaveProfile,

    // Notifications
    notifPrefs,
    loadingNotifPrefs,
    savingNotifPrefs,
    handleNotifPrefChange,
  };
}
